import { useEffect, useState } from "react";
import { FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import AdminPendingTasks from "../components/AdminPendingTasks";
import DailySummarySettings from "../components/DailySummarySettings";
import RolesPicker from "../components/RolesPicker";
import { useRoles } from "../lib/roles";

export default function AdminTasks() {
  const [form, setForm] = useState({
    break_discount_time: false,
    break_reasons: [],
    break_families: [],
  });
  // Settings fields we don't edit here but must pass through to PUT /settings
  const [passthrough, setPassthrough] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [roles] = useRoles();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setForm({
          break_discount_time: !!data.break_discount_time,
          break_reasons: Array.isArray(data.break_reasons)
            ? data.break_reasons.map((r) =>
                typeof r === "string"
                  ? { name: r, discounts: false, family_id: data.break_families?.[0]?.id || "", visible_roles: ["todos"], requires_pin: true }
                  : { name: r.name || "", discounts: !!r.discounts, family_id: r.family_id || "", visible_roles: r.visible_roles || ["todos"], requires_pin: r.requires_pin !== false }
              )
            : [],
          break_families: Array.isArray(data.break_families)
            ? data.break_families.map((f) => ({ id: f.id, name: f.name || "", color: f.color || "#94a3b8" }))
            : [],
        });
        setPassthrough({
          geofence_enabled: !!data.geofence_enabled,
          workplace_lat: data.workplace_lat ?? null,
          workplace_lng: data.workplace_lng ?? null,
          workplace_radius_m: data.workplace_radius_m ?? 50,
          invoice_upload_pin: data.invoice_upload_pin ?? "",
          hide_edit_delete_punch: !!data.hide_edit_delete_punch,
        });
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const missingFamily = (form.break_reasons || []).find((r) => (r?.name || "").trim() && !r?.family_id);
    if (missingFamily) {
      toast.error(`El motivo "${missingFamily.name}" necesita una familia asignada`);
      return;
    }
    if (!(form.break_families || []).length) {
      toast.error("Debe existir al menos una familia de tareas");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...passthrough,
        break_discount_time: !!form.break_discount_time,
        break_families: (form.break_families || [])
          .map((f) => ({ id: f.id || undefined, name: (f?.name || "").trim(), color: (f?.color || "#94a3b8").trim() }))
          .filter((f) => f.name),
        break_reasons: (form.break_reasons || [])
          .map((r) => ({
            name: (r?.name || "").trim(),
            discounts: !!r?.discounts,
            family_id: r?.family_id || "",
            visible_roles: r?.visible_roles?.length ? r.visible_roles : ["todos"],
            requires_pin: r?.requires_pin !== false,
          }))
          .filter((r) => r.name),
      };
      const { data } = await api.put("/settings", payload);
      setForm({
        break_discount_time: !!data.break_discount_time,
        break_families: (data.break_families || []).map((x) => ({ id: x.id, name: x.name, color: x.color })),
        break_reasons: (data.break_reasons || []).map((r) => ({
          name: r.name,
          discounts: !!r.discounts,
          family_id: r.family_id || "",
          visible_roles: r.visible_roles || ["todos"],
          requires_pin: r.requires_pin !== false,
        })),
      });
      toast.success("Tareas guardadas");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[var(--text-secondary)]">Cargando…</div>;

  return (
    <div className="max-w-5xl space-y-6" data-testid="admin-tasks-page">
      <div>
        <div className="label-cap">tareas</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Ajustes tareas</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-2">
          Define las familias, los motivos disponibles y a qué categoría de trabajadores se muestran.
        </p>
      </div>

      <AdminPendingTasks />
      <DailySummarySettings />

      <form onSubmit={save} className="space-y-6">
        <div className="card-flat p-6 rounded-2xl">
          <label className="inline-flex items-center gap-3 cursor-pointer mb-5">
            <input
              type="checkbox"
              checked={form.break_discount_time}
              onChange={(e) => onChange("break_discount_time", e.target.checked)}
              data-testid="break-discount-toggle"
              className="w-5 h-5 accent-[var(--primary)] cursor-pointer"
            />
            <div>
              <div className="font-display font-semibold text-sm">Descontar por defecto (motivos nuevos)</div>
              <div className="text-xs text-[var(--text-secondary)]">Valor inicial para nuevos motivos. Cada motivo tiene su propio interruptor.</div>
            </div>
          </label>

          {/* Families */}
          <div className="mb-6 p-4 rounded-xl bg-[var(--bg)] border border-[var(--border)]">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-cap mb-0.5">familias</div>
                <div className="text-xs text-[var(--text-secondary)]">Agrupa los motivos con un color para los informes.</div>
              </div>
              <button
                type="button"
                onClick={() => onChange("break_families", [...(form.break_families || []), { id: "", name: "", color: "#94a3b8" }])}
                data-testid="break-family-add"
                className="px-3 py-2 rounded-xl border border-[var(--border)] text-sm font-display font-semibold hover:bg-white"
              >
                + Añadir familia
              </button>
            </div>
            <div className="space-y-2">
              {(form.break_families || []).length === 0 && (
                <div className="text-xs text-[var(--text-secondary)] italic">Sin familias. Añade al menos una para poder guardar.</div>
              )}
              {(form.break_families || []).map((f, idx) => (
                <div key={f.id || `new-${idx}`} className="flex items-center gap-2 flex-wrap" data-testid={`break-family-row-${idx}`}>
                  <input
                    type="color"
                    value={f.color || "#94a3b8"}
                    onChange={(e) => {
                      const arr = [...(form.break_families || [])];
                      arr[idx] = { ...arr[idx], color: e.target.value };
                      onChange("break_families", arr);
                    }}
                    data-testid={`break-family-color-${idx}`}
                    className="w-10 h-10 rounded-lg border border-[var(--border)] cursor-pointer p-0.5 bg-white"
                    title="Color"
                  />
                  <input
                    type="text"
                    value={f.name}
                    onChange={(e) => {
                      const arr = [...(form.break_families || [])];
                      arr[idx] = { ...arr[idx], name: e.target.value };
                      onChange("break_families", arr);
                    }}
                    data-testid={`break-family-name-${idx}`}
                    className="flex-1 min-w-[180px] px-3 py-2 rounded-xl border border-[var(--border)] bg-white"
                    placeholder="Nombre familia (ej. Personal)"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const removed = form.break_families[idx];
                      const remainingFamilies = (form.break_families || []).filter((_, i) => i !== idx);
                      const updatedReasons = (form.break_reasons || []).map((r) =>
                        r.family_id === removed?.id ? { ...r, family_id: "" } : r
                      );
                      setForm((prev) => ({ ...prev, break_families: remainingFamilies, break_reasons: updatedReasons }));
                    }}
                    data-testid={`break-family-delete-${idx}`}
                    className="px-3 py-2 rounded-xl border border-[var(--border)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-white text-sm"
                  >
                    Quitar
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Reasons */}
          <div className="space-y-2">
            <div className="label-cap mb-1">motivos disponibles</div>
            <div className="text-xs text-[var(--text-secondary)] mb-2">
              <strong>Visibilidad</strong>: elige qué roles de trabajador ven cada tarea (o <strong>Todos</strong>). Los roles se crean en Trabajadores. El admin ve todas. Desmarca <strong>Identificarse</strong> para permitir iniciar/cerrar sin PIN si el trabajador ya está fichado.
            </div>
            {(form.break_reasons || []).map((r, idx) => (
              <div key={r.name || `new-${idx}`} className="flex items-center gap-2 flex-wrap" data-testid={`break-reason-row-${idx}`}>
                <input
                  type="text"
                  value={r.name}
                  onChange={(e) => {
                    const arr = [...(form.break_reasons || [])];
                    arr[idx] = { ...arr[idx], name: e.target.value };
                    onChange("break_reasons", arr);
                  }}
                  data-testid={`break-reason-input-${idx}`}
                  className="flex-1 min-w-[180px] px-3 py-2 rounded-xl border border-[var(--border)] bg-white"
                  placeholder="Motivo (ej. Fumar)"
                />
                <select
                  value={r.family_id || ""}
                  onChange={(e) => {
                    const arr = [...(form.break_reasons || [])];
                    arr[idx] = { ...arr[idx], family_id: e.target.value };
                    onChange("break_reasons", arr);
                  }}
                  data-testid={`break-reason-family-${idx}`}
                  className="px-3 py-2 rounded-xl border border-[var(--border)] bg-white text-sm font-display"
                  required
                >
                  <option value="">— Familia —</option>
                  {(form.break_families || []).map((f) => (
                    <option key={f.id || f.name} value={f.id || ""}>{f.name}</option>
                  ))}
                </select>
                <RolesPicker
                  roles={roles}
                  value={r.visible_roles}
                  testId={`break-reason-roles-${idx}`}
                  onChange={(vr) => {
                    const arr = [...(form.break_reasons || [])];
                    arr[idx] = { ...arr[idx], visible_roles: vr };
                    onChange("break_reasons", arr);
                  }}
                />
                <label className="inline-flex items-center gap-2 text-xs font-display font-semibold px-3 py-2 rounded-xl border border-[var(--border)] cursor-pointer bg-white">
                  <input
                    type="checkbox"
                    checked={!!r.discounts}
                    onChange={(e) => {
                      const arr = [...(form.break_reasons || [])];
                      arr[idx] = { ...arr[idx], discounts: e.target.checked };
                      onChange("break_reasons", arr);
                    }}
                    data-testid={`break-reason-discounts-${idx}`}
                    className="w-4 h-4 accent-[var(--primary)]"
                  />
                  Descuenta
                </label>
                <label
                  className="inline-flex items-center gap-2 text-xs font-display font-semibold px-3 py-2 rounded-xl border border-[var(--border)] cursor-pointer bg-white"
                  title="Si se desmarca, el trabajador ya fichado puede iniciar/cerrar la tarea sin introducir PIN"
                >
                  <input
                    type="checkbox"
                    checked={r.requires_pin !== false}
                    onChange={(e) => {
                      const arr = [...(form.break_reasons || [])];
                      arr[idx] = { ...arr[idx], requires_pin: e.target.checked };
                      onChange("break_reasons", arr);
                    }}
                    data-testid={`break-reason-requires-pin-${idx}`}
                    className="w-4 h-4 accent-[var(--primary)]"
                  />
                  Identificarse
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const arr = (form.break_reasons || []).filter((_, i) => i !== idx);
                    onChange("break_reasons", arr);
                  }}
                  data-testid={`break-reason-delete-${idx}`}
                  className="px-3 py-2 rounded-xl border border-[var(--border)] text-[var(--danger)] hover:bg-[var(--danger)] hover:text-white text-sm"
                >
                  Quitar
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => onChange("break_reasons", [
                ...(form.break_reasons || []),
                { name: "", discounts: !!form.break_discount_time, family_id: form.break_families?.[0]?.id || "", visible_roles: ["todos"], requires_pin: true },
              ])}
              data-testid="break-reason-add"
              className="mt-1 px-3 py-2 rounded-xl border border-[var(--border)] text-sm font-display font-semibold hover:bg-[var(--bg)]"
            >
              + Añadir motivo
            </button>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              Si añades <strong>“Otros”</strong>, el trabajador podrá escribir un texto libre al iniciar la tarea.
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            data-testid="tasks-save"
            className="btn-primary px-6 py-3 rounded-xl font-display font-bold flex items-center gap-2 disabled:opacity-50"
          >
            <FloppyDisk size={18} weight="duotone" />
            {saving ? "Guardando…" : "Guardar tareas"}
          </button>
        </div>
      </form>
    </div>
  );
}
